import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEmailTemplateList } from "./useEmailTemplateList";
import { useEmailTemplateVariables } from "./useEmailTemplateVariables";
import { useSendDirectEmail } from "./useSendDirectEmail";
import {
    BUTTON_LABELS,
    LOAD_FAILURE_MESSAGES,
    LOADING_LABELS,
    SAMPLE_DEFAULTS,
} from "./constants";
import {
    TEST_EMAIL_TEMPLATE_FORM_SCHEMA,
    buildSendDirectEmailRequestBody,
    getInitialFormValues,
    getInitialVariableValues,
} from "./testEmailTemplateSchema";
import "./TestEmailTemplate.scss";

/**
 * Test Email Template form.
 * Lets operators dispatch a pre-filled test email through the notifications service
 * to verify that a chosen template renders and is queued for delivery.
 * @returns {import('react').JSX.Element}
 */
const TestEmailTemplate = () => {
    const {
        templates,
        isLoading: isTemplateListLoading,
        isError: isTemplateListError,
        refetch: refetchTemplateList,
    } = useEmailTemplateList();

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(TEST_EMAIL_TEMPLATE_FORM_SCHEMA),
        defaultValues: {
            template: "",
            toEmail: "",
            recipientId: "",
            subjects: SAMPLE_DEFAULTS.FALLBACK_SUBJECT,
            message: SAMPLE_DEFAULTS.MESSAGE,
            variables: {},
        },
    });

    const selectedTemplate = watch("template");

    const {
        variables: variableTypes,
        subject: currentTemplateSubject,
        isLoading: isVariablesLoading,
        isError: isVariablesError,
        refetch: refetchVariables,
    } = useEmailTemplateVariables(selectedTemplate);

    const { mutate, isPending } = useSendDirectEmail({
        onSuccess: () => setValue("toEmail", ""),
    });

    const isMetadataLoading = isTemplateListLoading || isVariablesLoading;
    const isMetadataError = isTemplateListError || isVariablesError;
    const isSendDisabled = isMetadataLoading || isMetadataError || isPending;

    useEffect(() => {
        if (templates.length > 0 && !selectedTemplate) {
            const firstTemplate = templates[0].template;
            setValue("template", firstTemplate);
        }
    }, [templates, selectedTemplate, setValue]);

    useEffect(() => {
        if (!selectedTemplate) return;
        setValue(
            "subjects",
            currentTemplateSubject ?? SAMPLE_DEFAULTS.FALLBACK_SUBJECT
        );
        setValue("variables", getInitialVariableValues(variableTypes));
    }, [selectedTemplate, variableTypes, currentTemplateSubject, setValue]);

    const handleValidSubmit = (formValues) => {
        const body = buildSendDirectEmailRequestBody(formValues, variableTypes);
        mutate(body);
    };

    const handleReset = () => {
        reset(
            getInitialFormValues({
                firstTemplate: selectedTemplate,
                currentTemplateVariables: variableTypes,
                currentTemplateSubject,
            })
        );
    };

    return (
        <div className="test-email-template">
            <form
                className="test-email-template__form"
                onSubmit={handleSubmit(handleValidSubmit)}
                noValidate
            >
                <div className="test-email-template__field">
                    <label
                        className="test-email-template__label"
                        htmlFor="tet-template"
                    >
                        Template
                    </label>
                    {isTemplateListLoading && (
                        <span
                            className="test-email-template__inline-loading"
                            role="status"
                            aria-live="polite"
                        >
                            <span className="loading-with-label">
                                Loading templates
                                <span
                                    className="loader loader--inline"
                                    aria-hidden="true"
                                />
                            </span>
                        </span>
                    )}
                    {!isTemplateListLoading && (
                        <select
                            id="tet-template"
                            className="test-email-template__select"
                            aria-describedby={
                                errors.template ? "tet-template-error" : undefined
                            }
                            {...register("template")}
                        >
                            {templates.map((t) => (
                                <option key={t.template} value={t.template}>
                                    {t.template}
                                </option>
                            ))}
                        </select>
                    )}
                    {errors.template && (
                        <span
                            id="tet-template-error"
                            className="test-email-template__error"
                            aria-live="polite"
                        >
                            {errors.template.message}
                        </span>
                    )}
                </div>

                <div className="test-email-template__field">
                    <label
                        className="test-email-template__label"
                        htmlFor="tet-toEmail"
                    >
                        Recipient email
                    </label>
                    <input
                        id="tet-toEmail"
                        type="email"
                        className="test-email-template__input"
                        aria-describedby={
                            errors.toEmail ? "tet-toEmail-error" : undefined
                        }
                        {...register("toEmail")}
                    />
                    {errors.toEmail && (
                        <span
                            id="tet-toEmail-error"
                            className="test-email-template__error"
                            aria-live="polite"
                        >
                            {errors.toEmail.message}
                        </span>
                    )}
                </div>

                <div className="test-email-template__field">
                    <label
                        className="test-email-template__label"
                        htmlFor="tet-recipientId"
                    >
                        Recipient ID{" "}
                        <span className="test-email-template__optional">
                            (optional — leave blank to use environment default)
                        </span>
                    </label>
                    <input
                        id="tet-recipientId"
                        type="text"
                        className="test-email-template__input"
                        {...register("recipientId")}
                    />
                </div>

                <div className="test-email-template__field">
                    <label
                        className="test-email-template__label"
                        htmlFor="tet-subjects"
                    >
                        Subject
                    </label>
                    <input
                        id="tet-subjects"
                        type="text"
                        className="test-email-template__input"
                        aria-describedby={
                            errors.subjects ? "tet-subjects-error" : undefined
                        }
                        {...register("subjects")}
                    />
                    {errors.subjects && (
                        <span
                            id="tet-subjects-error"
                            className="test-email-template__error"
                            aria-live="polite"
                        >
                            {errors.subjects.message}
                        </span>
                    )}
                </div>

                <div className="test-email-template__field">
                    <label
                        className="test-email-template__label"
                        htmlFor="tet-message"
                    >
                        Message
                    </label>
                    <textarea
                        id="tet-message"
                        className="test-email-template__textarea"
                        rows={4}
                        aria-describedby={
                            errors.message ? "tet-message-error" : undefined
                        }
                        {...register("message")}
                    />
                    {errors.message && (
                        <span
                            id="tet-message-error"
                            className="test-email-template__error"
                            aria-live="polite"
                        >
                            {errors.message.message}
                        </span>
                    )}
                </div>

                <fieldset className="test-email-template__variables-block">
                    <legend className="test-email-template__label">
                        Template variables
                    </legend>

                    {isVariablesLoading && (
                        <div
                            className="test-email-template__inline-loading"
                            role="status"
                            aria-live="polite"
                        >
                            <span className="loading-with-label">
                                {LOADING_LABELS.TEMPLATE_VARIABLES}
                                <span
                                    className="loader loader--inline"
                                    aria-hidden="true"
                                />
                            </span>
                        </div>
                    )}

                    {isVariablesError && !isVariablesLoading && (
                        <div
                            className="test-email-template__load-error"
                            role="alert"
                        >
                            <span>{LOAD_FAILURE_MESSAGES.TEMPLATE_VARIABLES}</span>
                            <button
                                type="button"
                                className="test-email-template__retry-btn"
                                onClick={() => refetchVariables()}
                            >
                                {BUTTON_LABELS.RETRY}
                            </button>
                        </div>
                    )}

                    {!isVariablesLoading &&
                        !isVariablesError &&
                        Object.entries(variableTypes).map(([variableName, type]) => {
                            const isBoolean = type === "boolean";
                            return (
                            
                                <div
                                    key={variableName}
                                    className={
                                        isBoolean
                                            ? "test-email-template__field test-email-template__field--checkbox"
                                            : "test-email-template__field"
                                    }
                                >
                                    {isBoolean ? (
                                        <>
                                            <input
                                                id={`tet-var-${variableName}`}
                                                type="checkbox"
                                                className="test-email-template__checkbox"
                                                {...register(`variables.${variableName}`)}
                                            />
                                            <label
                                                className="test-email-template__label"
                                                htmlFor={`tet-var-${variableName}`}
                                            >
                                                {variableName}
                                            </label>
                                        </>
                                    ) : (
                                        <>
                                            <label
                                                className="test-email-template__label"
                                                htmlFor={`tet-var-${variableName}`}
                                            >
                                                {variableName}
                                            </label>
                                            <input
                                                id={`tet-var-${variableName}`}
                                                type="text"
                                                className="test-email-template__input"
                                                {...register(`variables.${variableName}`)}
                                            />
                                        </>
                                    )}
                                </div>
                            );
                        }
                        )}
                </fieldset>

                {isTemplateListError && (
                    <div
                        className="test-email-template__load-error"
                        role="alert"
                    >
                        <span>{LOAD_FAILURE_MESSAGES.TEMPLATES}</span>
                        <button
                            type="button"
                            className="test-email-template__retry-btn"
                            onClick={() => refetchTemplateList()}
                        >
                            {BUTTON_LABELS.RETRY}
                        </button>
                    </div>
                )}

                <div className="test-email-template__actions">
                    <button
                        type="submit"
                        className="test-email-template__send-btn"
                        disabled={isSendDisabled}
                        aria-busy={isPending}
                    >
                        {isPending ? BUTTON_LABELS.SENDING : BUTTON_LABELS.SEND}
                    </button>
                    <button
                        type="button"
                        className="test-email-template__reset-btn"
                        onClick={handleReset}
                        disabled={isPending}
                    >
                        {BUTTON_LABELS.RESET}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TestEmailTemplate;
